import {useCallback, useEffect, useMemo, useState} from 'react';

import {useGetQuestionSampleSetDetailQuery} from '~/store/rtkQuery';
import {useJsonDataFromStorage} from '../hooks';
import {
  QuestionMatchingEvaluationResponse as QuestionMatchingEvaluationResponseType,
  QuestionSampleSetQuestionData,
} from '../AiSupport.types';
import {getEvaluationScore} from '../AiSupport.utils';
import {QuestionMatchCandidates} from '../QuestionMatchingEvaluation/QuestionMatchCandidates';
import {IS_PROD} from '~/modules/config';

export const QuestionMatchingEvaluationResponse = ({
  questionSampleSetId,
  questionMatchingEvaluationId,
}: {
  questionSampleSetId: string;
  questionMatchingEvaluationId: string;
}) => {
  const {data, isLoading, error} = useGetQuestionSampleSetDetailQuery({
    id: questionSampleSetId,
  });

  const sampleDataFilePath = data?.sample.data_file_path ?? '';

  const {
    data: sampleData,
    isFetching,
    fetchFileData,
  } = useJsonDataFromStorage<QuestionSampleSetQuestionData[]>({
    storageFilePath: sampleDataFilePath,
  });

  const [inViewResponse, setInViewResponse] =
    useState<QuestionMatchingEvaluationResponseType | null>(null);
  const inViewQuestion = useMemo(() => {
    return sampleData?.find(q => q.id === inViewResponse?.question_id) ?? null;
  }, [sampleData, inViewResponse]);

  const evaluationResponse = useMemo(() => {
    return (
      data?.questionMatchingEvaluationResponses.find(
        r => r.id === questionMatchingEvaluationId,
      ) ?? null
    );
  }, [data, questionMatchingEvaluationId]);

  useEffect(() => {
    if (sampleDataFilePath) {
      fetchFileData();
    }
  }, [sampleDataFilePath, fetchFileData]);

  const onEscapeKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setInViewResponse(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onEscapeKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onEscapeKeyDown, false);
    };
  }, [onEscapeKeyDown]);

  if (error) {
    return (
      <div role="alert" className="alert alert-error my-4">
        <span>{error.message}</span>
      </div>
    );
  }

  if (isLoading || isFetching) {
    return (
      <div className="flex flex-col items-center justify-center p-10">
        <span className="loading loading-dots loading-lg"></span>
        <p>Loading</p>
      </div>
    );
  }

  if (!evaluationResponse) {
    return (
      <div>
        <p>Not found</p>
      </div>
    );
  }

  return (
    <div>
      <div className="p-4">
        <h2 className="text-lg font-bold">
          Score: {getEvaluationScore(evaluationResponse.response)}
        </h2>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Question</th>
            <th>Matched Question</th>
            <th>Match Index</th>
            <th>Comments</th>
          </tr>
        </thead>
        {sampleData?.map(question => {
          const evaluation = evaluationResponse.response[question.id];
          const matchedCandidate =
            evaluation?.best_match_index >= 0
              ? question.nearest_neighbours[evaluation?.best_match_index]
              : null;
          return (
            <tr key={question.id}>
              <td>{question.dato_record.question}</td>
              <td>{matchedCandidate?.question.question ?? 'No Match'}</td>
              <td>{evaluation?.best_match_index ?? '-'}</td>
              <td>{evaluation?.comments ?? '-'}</td>
              <td>
                <button
                  className="btn"
                  onClick={() => setInViewResponse(evaluation)}>
                  Candidates
                </button>
              </td>
            </tr>
          );
        })}
      </table>

      <div
        className={'modal ' + (!!inViewQuestion ? 'modal-open' : '')}
        role="dialog">
        {!!inViewQuestion && !!inViewResponse && (
          <div className="modal-box w-11/12 max-w-5xl">
            <div className="mb-4">
              <h3 className="font-bold text-lg mb-2">
                {inViewQuestion.dato_record.question}
              </h3>
              <h4 className="font-semibold text-md mb-2">
                Best Match Index:{' '}
                <span className="badge badge-secondary">
                  {inViewResponse.best_match_index}
                </span>
              </h4>
              <div>
                <a
                  className="link"
                  href={
                    IS_PROD
                      ? `https://luna-prod.admin.datocms.com/editor/item_types/61134/items/${inViewQuestion.id}`
                      : `https://luna-poc.admin.datocms.com/editor/item_types/730156/items/${inViewQuestion.id}`
                  }
                  target="_blank"
                  rel="noreferrer">
                  {inViewResponse?.question_id}
                </a>
              </div>
            </div>

            <div>
              <QuestionMatchCandidates
                selectedIndex={inViewResponse.best_match_index}
                candidates={inViewQuestion.nearest_neighbours}
              />

              <div className="my-3">{inViewResponse.comments ?? '-'}</div>
            </div>
            <div className="modal-action">
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setInViewResponse(null);
                }}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
